import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useLocalizedCountries } from '@/hooks/useLocalizedCountries';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { Button } from '@heidi/design-system/button';
import { useIsExperimentalDesignSystemForms } from '@heidi/design-system/hooks/use-is-experimental-design-system-forms';
import { type Region, TemplateCategory } from '@heidi/schema/template-gallery';
import { Filter } from '@heidi/templates';
import { FilterSettings, TemplateCommunitySortBy } from '@heidi/templates/contracts';
import { countries } from '@heidi/utils/countries';
import { ClipboardList, Globe2, List, ListRestart, Stethoscope } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  communityFiltersMessages,
  getCommunityFilterAllLabel,
  getTemplateCategoryFilterLabel,
  getTemplateCommunitySortLabel,
} from './communityFilters.messages';

const Filters = () => {
  const intl = useIntl();
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  const { localizedCountries } = useLocalizedCountries();
  const { data: specialties } = useQuerySpecialties((data) => data.specialties);

  const {
    sort,
    setSort,
    location,
    setLocation,
    specialty,
    setSpecialty,
    category,
    setCategory,
    resetFilters,
  } = useUrlFilters();

  const hasActiveFilters =
    (location !== String(FilterSettings.ALL) &&
      location !== String(FilterSettings.INITIAL) &&
      location !== String(FilterSettings.UNDEFINED)) ||
    category !== FilterSettings.ALL ||
    (specialty !== String(FilterSettings.ALL) &&
      specialty !== String(FilterSettings.INITIAL) &&
      specialty !== String(FilterSettings.UNDEFINED));

  const allFilterLabel = getCommunityFilterAllLabel(intl);

  return (
    <div className="flex w-full flex-wrap justify-center gap-2">
      <Filter
        onChange={(value) => setSort(value as TemplateCommunitySortBy)}
        useCommand={false}
        icon={<List size={16} />}
        title={intl.formatMessage(communityFiltersMessages.sort)}
        values={Object.values(TemplateCommunitySortBy)}
        labels={Object.values(TemplateCommunitySortBy).map((sortValue) =>
          getTemplateCommunitySortLabel(intl, sortValue)
        )}
        value={sort}
        name="sort"
      />
      <Filter
        onChange={(value) => setLocation(value as Region)}
        useCommand={true}
        icon={<Globe2 size={16} />}
        title={intl.formatMessage(communityFiltersMessages.location)}
        labels={[
          allFilterLabel,
          ...localizedCountries.map((country) => country.localizedName),
        ]}
        values={[FilterSettings.ALL, ...countries.map((country) => country.name)]}
        value={
          location === String(FilterSettings.UNDEFINED) ? FilterSettings.ALL : location
        }
        name="location"
      />
      <Filter
        onChange={(value) => setSpecialty(value)}
        useCommand={true}
        icon={<Stethoscope size={16} />}
        title={intl.formatMessage(communityFiltersMessages.specialty)}
        values={[FilterSettings.ALL, ...(specialties?.map((s) => s.name) ?? [])]}
        value={
          specialty === String(FilterSettings.UNDEFINED) ? FilterSettings.ALL : specialty
        }
        labels={[allFilterLabel, ...(specialties?.map((s) => s.localizedName) ?? [])]}
        name="specialty"
      />
      <Filter
        onChange={(value) => setCategory(value as TemplateCategory | FilterSettings.ALL)}
        useCommand={false}
        icon={<ClipboardList size={16} />}
        title={intl.formatMessage(communityFiltersMessages.category)}
        values={[
          FilterSettings.ALL,
          TemplateCategory.DOCUMENT_TEMPLATE,
          TemplateCategory.CONSULT_NOTE_TEMPLATE,
          TemplateCategory.FORM_TEMPLATE,
        ]}
        value={category}
        labels={[
          allFilterLabel,
          getTemplateCategoryFilterLabel(intl, TemplateCategory.DOCUMENT_TEMPLATE),
          getTemplateCategoryFilterLabel(intl, TemplateCategory.CONSULT_NOTE_TEMPLATE),
          getTemplateCategoryFilterLabel(intl, TemplateCategory.FORM_TEMPLATE),
        ]}
        name="category"
      />
      {hasActiveFilters && (
        <Button
          size={isExperimentalForms ? 'md' : 'lg'}
          disableAutomaticSvgSizing
          variant="ghost"
          className="text-text-secondary"
          onClick={resetFilters}
        >
          <span>
            <FormattedMessage {...communityFiltersMessages.resetFilters} />
          </span>
          <ListRestart size={16} />
        </Button>
      )}
    </div>
  );
};

export default Filters;
